import * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import styles from "./button-chip-filter.module.css";

const chipFilterButton = tv({
  base: [
    "inline-flex items-center justify-center gap-2 border border-neutral-500 bg-transparent font-montserrat transition duration-300 ease-out",
    "hover:border-secondary-500 hover:bg-secondary-200 hover:text-secondary-500",
    "active:bg-primary-100 active:text-neutral-500",
    "group min-w-fit font-montserrat",
  ].join(" "),
  variants: {
    size: {
      lg: "!rounded-full px-6 py-2 text-body-1-400",
      md: "!rounded-full px-4 py-2 text-body-1-400",
      sm: "!rounded-full px-3 py-2 text-body-2-300",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type ChipFilterButtonVariant = VariantProps<typeof chipFilterButton>;

export interface ChipFilterButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ChipFilterButtonVariant {
  asChild?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  value?: string | number;
}

const ChipFilterButton = React.forwardRef<
  HTMLButtonElement,
  ChipFilterButtonProps
>(
  (
    {
      className,
      size,
      disabled,
      children,
      asChild,
      leadingIcon,
      trailingIcon,
      value,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(styles.button, chipFilterButton({ size, className }))}
        ref={ref}
        data-value={value}
        {...props}
      >
        {leadingIcon && (
          <span className="mb-1 inline-flex shrink-0">{leadingIcon}</span>
        )}
        <span className="inline-block">{children}</span>
        {trailingIcon && (
          <span className="mb-1 inline-flex shrink-0">{trailingIcon}</span>
        )}
      </Comp>
    );
  }
);
ChipFilterButton.displayName = "ChipFilterButton";

export default ChipFilterButton;
